import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { FaRoute, FaTruck, FaUsers } from "react-icons/fa"
import { Menu } from "../../components/Menu/Menu"
import { getValueCookie } from '../../services/cookieService'
import { COLORS, Loading, NotAllowed, PageHero, Tabs, Toasts } from './FieldOpsKit'
import { DEMO_USERS } from './demoData'
import { actorOf, totalManagerUnread } from './store'
import { getPref, useDemoSession } from './demoSession'
import { watchMenuHeight } from '../../functions/menuOffset'
import FieldApp from './field/FieldApp'
import RoutesTab from './sales/RoutesTab'
import GoalsTab from './sales/GoalsTab'
import AgendaTab from './sales/AgendaTab'
import ComplianceTab from './sales/ComplianceTab'
import UnroutedTab from './sales/UnroutedTab'
import TeamSummaryTab from './sales/TeamSummaryTab'
import LiveMapTab from './team/LiveMapTab'
import MessagesTab from './team/MessagesTab'
import ReturnsTab from './team/ReturnsTab'
import BoardTab from './drivers/BoardTab'
import ReadyTab from './drivers/ReadyTab'
import FleetTab from './drivers/FleetTab'
import ReportTab from './drivers/ReportTab'
import '../../css/dashboard.css'

/**
 * Field Ops (demo). Every screen has its own URL so each one can be placed in the side menu on its own and a reload
 * opens the same place:
 *
 *   /field-ops/seller-panel/:tab   what a salesperson sees (route, stores, history, chat, me)
 *   /field-ops/driver-panel/:tab   what a driver sees      (route, history, chat, me)
 *   /field-ops/sales/:tab          administration: routes, goals, agenda, compliance, unrouted stores
 *   /field-ops/drivers/:tab        administration: delivery board, ready to deliver, drivers & vehicles, report
 *   /field-ops/team/:tab           administration: team summary, live map, messages, returns
 *
 * (Who acts in a panel comes from ?as=rep-RT / ?as=drv-d1 in the URL; there is no picker on screen.)
 * The demo data lives in memory and is shared by all of them: the actions are logged in sessionStorage and replayed
 * on every page (demoSession.js), so something done in the seller panel shows in the admin pages of the same browser
 * tab. Admin-only for now (TypeUser '1'); the real permission checks will live in the backend.
 */

export const SELLER_BASE = '/field-ops/seller-panel'
export const DRIVER_BASE = '/field-ops/driver-panel'

const ADMIN_MODULES = {
    sales: {
        base: '/field-ops/sales', title: 'Sales Reps', sub: 'Routes, goals, agenda and visit compliance', icon: <FaRoute />, first: 'routes',
        tabs: () => [{ key: 'routes', label: 'Sales Routes' }, { key: 'goals', label: 'Goals' }, { key: 'agenda', label: 'Agenda setup' }, { key: 'compliance', label: 'Visit compliance' }, { key: 'unrouted', label: 'Unrouted stores' }],
    },
    drivers: {
        base: '/field-ops/drivers', title: 'Drivers', sub: 'Deliveries, drivers and vehicles', icon: <FaTruck />, first: 'board',
        tabs: () => [{ key: 'board', label: 'Delivery board' }, { key: 'ready', label: 'Ready to deliver' }, { key: 'fleet', label: 'Drivers & vehicles' }, { key: 'report', label: 'Delivery report' }],
    },
    team: {
        base: '/field-ops/team', title: 'Field team', sub: 'Compliance by person, route and store · live map · messages · returns', icon: <FaUsers />, first: 'summary',
        tabs: (unread, waiting) => [{ key: 'summary', label: 'Team summary' }, { key: 'live', label: 'Live map' }, { key: 'messages', label: 'Messages', badge: unread || null }, { key: 'returns', label: 'Returns', badge: waiting || null }],
    },
}
const PANELS = {
    rep: { base: SELLER_BASE, title: 'Seller panel', loading: 'Loading your day...', tabs: ['route', 'stores', 'history', 'chat', 'me'] },
    driver: { base: DRIVER_BASE, title: 'Driver panel', loading: 'Loading your deliveries...', tabs: ['route', 'history', 'chat', 'me'] },
}

/** Same loading flow the real endpoints will have, plus the toast stack. */
const useShell = () => {
    const [loading, setLoading] = useState(true)
    const [toasts, setToasts] = useState([])
    const toastId = useRef(0)
    useEffect(() => {
        const t = setTimeout(() => setLoading(false), 450)
        return () => clearTimeout(t)
    }, [])
    useEffect(() => watchMenuHeight('.fo-root', '--fo-menu-h'), [])
    const toast = (text, tone) => {
        toastId.current += 1
        const id = toastId.current
        setToasts((list) => list.concat([{ id: id, text: text, tone: tone || 'ok' }]))
        setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), 3800)
    }
    const dismiss = (id) => setToasts((list) => list.filter((t) => t.id !== id))
    return { loading: loading, toasts: toasts, toast: toast, dismiss: dismiss }
}

const makeCtx = (state, dispatch, me, toast) => ({
    state: state, dispatch: dispatch, me: me, toast: toast,
    can: {
        editGoals: me.kind === 'admin' && (me.role === 'super' || me.role === 'editor'),
        approve: me.kind === 'admin' && (me.role === 'super' || me.role === 'approver'),
        dispatch: me.kind === 'admin',
    },
})

const NotAdminPage = () => (
    <div className="dash-page fo-root" style={{ paddingTop: '70px' }}>
        <Menu />
        <NotAllowed text="Field Ops is available to administrators only." />
    </div>
)

/* ======================================================================================================
 * ADMINISTRATION PAGES  (/field-ops/sales/:tab, /field-ops/drivers/:tab, /field-ops/team/:tab)
 * ==================================================================================================== */
export function FieldOpsAdminPage({ module }) {
    const isAdmin = getValueCookie('TypeUser') === '1'
    const def = ADMIN_MODULES[module]
    const { tab } = useParams()
    const navigate = useNavigate()
    const [state, dispatch] = useDemoSession()
    const shell = useShell()
    const admins = DEMO_USERS.filter((u) => u.kind === 'admin')
    const me = admins.filter((u) => u.id === 'pablo')[0] // administration always acts as the super admin
    const ctx = useMemo(() => makeCtx(state, dispatch, me, shell.toast), [state]) // eslint-disable-line react-hooks/exhaustive-deps
    const location = useLocation()
    // /…?reset=1 brings the original demo data back (there is no Reset button on screen)
    useEffect(() => {
        if (new URLSearchParams(location.search).get('reset') === '1') { dispatch({ type: 'RESET' }); navigate(location.pathname, { replace: true }) }
    }, [location.search]) // eslint-disable-line react-hooks/exhaustive-deps

    if (!isAdmin) return <NotAdminPage />
    const unread = totalManagerUnread(state.field)
    const waiting = state.field.returns.filter((r) => r.status === 'requested').length
    const tabList = def.tabs(unread, waiting)
    if (!tabList.some((t) => t.key === tab)) return <Navigate to={def.base + '/' + def.first} replace />

    const go = (m, t) => navigate(ADMIN_MODULES[m].base + '/' + (t || ADMIN_MODULES[m].first))

    const switcher = (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {Object.keys(ADMIN_MODULES).map((k) => {
                const m = ADMIN_MODULES[k]
                const on = module === k
                const extra = k === 'team' && unread + waiting ? ' (' + (unread + waiting) + ')' : ''
                return (
                    <button key={k} type="button" onClick={() => go(k)}
                        style={{ background: on ? '#FFFFFF' : 'rgba(255,255,255,0.12)', color: on ? COLORS.navy : '#FFFFFF', border: 'none', borderRadius: 8, padding: '0.45rem 0.9rem', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
                        <span style={{ marginRight: 6 }}>{m.icon}</span>{m.title}{extra}
                    </button>
                )
            })}
        </div>
    )
    let content = null
    if (!shell.loading) {
        if (module === 'sales') {
            if (tab === 'routes') content = <RoutesTab ctx={ctx} />
            else if (tab === 'goals') content = <GoalsTab ctx={ctx} />
            else if (tab === 'agenda') content = <AgendaTab ctx={ctx} />
            else if (tab === 'compliance') content = <ComplianceTab ctx={ctx} />
            else content = <UnroutedTab ctx={ctx} />
        } else if (module === 'team') {
            if (tab === 'summary') content = <TeamSummaryTab ctx={ctx} />
            else if (tab === 'live') content = <LiveMapTab ctx={ctx} />
            else if (tab === 'messages') content = <MessagesTab ctx={ctx} />
            else content = <ReturnsTab ctx={ctx} />
        } else {
            if (tab === 'board') content = <BoardTab ctx={ctx} />
            else if (tab === 'ready') content = <ReadyTab ctx={ctx} goBoard={() => go('drivers', 'board')} />
            else if (tab === 'fleet') content = <FleetTab ctx={ctx} />
            else content = <ReportTab ctx={ctx} />
        }
    }

    return (
        <div className="dash-page fo-root" style={{ paddingTop: '70px' }}>
            <Menu />
            <PageHero icon={def.icon} title={def.title} subtitle={def.sub}
                right={switcher} />
            <div className="container-fluid" style={{ marginTop: 18 }}>
                <Tabs items={tabList} active={tab} onChange={(k) => go(module, k)} />
                {shell.loading ? <Loading text="Loading..." /> : content}
            </div>
            <Toasts toasts={shell.toasts} onDismiss={shell.dismiss} />
        </div>
    )
}

/* ======================================================================================================
 * FIELD PANELS  (/field-ops/seller-panel/:tab, /field-ops/driver-panel/:tab)
 * ==================================================================================================== */
export function FieldOpsPanelPage({ kind }) {
    const isAdmin = getValueCookie('TypeUser') === '1'
    const def = PANELS[kind]
    const { tab } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const [search] = useSearchParams()
    const [state, dispatch] = useDemoSession()
    const shell = useShell()
    const users = DEMO_USERS.filter((u) => u.kind === kind)
    const asParam = search.get('as')
    const asId = users.some((u) => u.id === asParam) ? asParam : (users.some((u) => u.id === getPref(kind, '')) ? getPref(kind, '') : users[0].id)
    const me = users.filter((u) => u.id === asId)[0]
    const ctx = useMemo(() => makeCtx(state, dispatch, me, shell.toast), [state, asId]) // eslint-disable-line react-hooks/exhaustive-deps

    if (!isAdmin) return <NotAdminPage />
    if (def.tabs.indexOf(tab) < 0) return <Navigate to={def.base + '/route' + location.search} replace />

    const onTab = (k) => navigate(def.base + '/' + k + location.search)

    return (
        <div className="dash-page fo-root" style={{ paddingTop: '70px' }}>
            <Menu />
            {shell.loading ? <Loading text={def.loading} /> : <FieldApp ctx={ctx} actor={actorOf(me)} tab={tab} onTab={onTab} />}
            <Toasts toasts={shell.toasts} onDismiss={shell.dismiss} />
        </div>
    )
}

export const FieldOpsSales = () => <FieldOpsAdminPage module="sales" />
export const FieldOpsDrivers = () => <FieldOpsAdminPage module="drivers" />
export const FieldOpsTeam = () => <FieldOpsAdminPage module="team" />
export const FieldOpsSeller = () => <FieldOpsPanelPage kind="rep" />
export const FieldOpsDriver = () => <FieldOpsPanelPage kind="driver" />
